import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import tinycolor from 'tinycolor2';
import { isIOS } from 'react-device-detect';

import * as QPlayerSVGElement from '../QPlayerSVGElement';
import { joinClassNames, STORY_CONFIG_ID } from '../../util/util';
import { NumberHelper } from '../../util/number-helper';

import ControlButton from './ControlButton';
import TopControls from './TopControls/TopControls';
import BottomControls from './BottomControls/BottomControls';
import CenterControls from './CenterControls/CenterControls';
import { StoryTopControl } from './TopControls/StoryTopControl';
import { SideControls } from './TopControls/SideControls';
import { StoryCenterControls } from './CenterControls/StoryCenterControls';

import './Controls.scss';

interface ControlsProps {
	settings: any;
	playerHover?: boolean;
	queryParams?: any;
	isMobile?: boolean;
	playerId?: string;
	isInlinePlayer?: boolean;
	isMaintainProportions?: boolean;
	onTouchStart?: (_: React.TouchEvent) => void;
	onTouchMove?: (_: React.TouchEvent) => void;
	onMouseEnter?: (_: React.MouseEvent) => void;
	onMouseLeave?: (_: React.MouseEvent) => void;
	onFocus?: (_: React.FocusEvent) => void;
	onBlur?: (_: React.FocusEvent) => void;
	alignVideoOnAndroidDevices?: (_timeOut: number, _isReset: boolean) => void;
}

const Controls: React.FC<ControlsProps> = ({
	settings,
	playerHover,
	queryParams = {},
	isMobile,
	isInlinePlayer,
	isMaintainProportions,
	onTouchStart,
	onTouchMove,
	onMouseEnter,
	onMouseLeave,
	onFocus,
	onBlur,
	alignVideoOnAndroidDevices,
}) => {
	const { t } = useTranslation();

	const { adsPlaying, isPlaying, currentVolumeLevel, isLiveStopped, currentPosition } = useSelector((state) => (state as any).playerReducer);
	const { currentMediaId, playlistId, showCountdownModule, mediaType, embedShareSettings, minimizedPlayer, mediasData } = useSelector((state) => (state as any).configurationReducer);

	const currentMedia = useMemo(() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId), [mediasData, currentMediaId]);
	const isStoryPlayer = typeof queryParams?.configId === "string" && queryParams.configId.toLowerCase().includes(STORY_CONFIG_ID.toLowerCase());
	const color = useMemo(() => settings?.playerStyling?.primaryColor ?? '#126cfc', [settings?.playerStyling?.primaryColor]);
	const opacity = Number(settings?.playerStyling?.primaryOpacity ?? 100);
	const rgbaWithOpa = { ...tinycolor(color).toRgb(), a: opacity / 100 };
	const hoverStyle = isMobile ? {} : { backgroundColor: tinycolor(rgbaWithOpa).toRgbString() };

	const showTopControls = useMemo(
		() =>
			settings?.topControls?.enabled ||
			settings?.controls?.enabled ||
			queryParams.sharing === 'true' ||
			queryParams.downloadMedia === 'true' ||
			queryParams.openInNewWindow === 'true',
		[queryParams, settings?.topControls?.enabled, settings?.controls?.enabled]
	);

	const [showUnmuteButton, setShowUnmuteButton] = useState(
		settings?.playbackOptions.showUnmuteBtn !== false &&
			(queryParams.mute === 'true' || (!queryParams.mute && settings?.playbackOptions.muteAtStart)) &&
			currentVolumeLevel === 0
	);

	const onUnmuteClick = useCallback(() => {
		setTimeout(() => {
			const qplayerAppElem = document.querySelector('div#main-container') as any;
			qplayerAppElem?.focus();
		});

		if (isIOS) {
			// IOSs sometime does not trigger click event. It seem the touch end event swallows onClick event. So handle volume on App.tsx for IOS.
			return;
		}

		const currentPlayerId = `player-${playlistId || currentMediaId}`;
		(window as any).GoBrain.widgets(currentPlayerId)?.volume?.(0.5);
	}, [currentMediaId, playlistId]);

	const calculatePlayerSize = useCallback(() => {
		if (!currentMedia || !currentMedia.videoWidth || !currentMedia.videoHeight) {
			return;
		}

		const videoRatio = Math.floor((currentMedia.videoHeight / currentMedia.videoWidth) * 100) / 100;
		const shouldCalculateTheSize =
			videoRatio === Math.floor((16 / 9) * 100) / 100 ||
			queryParams?.fromQplayerWidget === 'true' ||
			(queryParams?.useLoader === 'true' && isStoryPlayer)
				? true
				: isMobile;
		if (!shouldCalculateTheSize) {
			return;
		}

		const currentPlayerId = `player-${isStoryPlayer ? currentMediaId : playlistId || currentMediaId}`;
		if (
			currentPlayerId &&
			!isInlinePlayer &&
			(isMobile || queryParams?.fromQplayerWidget === 'true' || (queryParams?.useLoader === 'true' && isStoryPlayer))
		) {
			setTimeout(() => {
				const playerElem = document.getElementById(currentPlayerId);
				const transitionElem = document.querySelector('.transition-wrapper') as any;
				const interactContainer = document.querySelector('.interactOverlayContainer') as any;
				if (!playerElem) {
					return;
				}

				const screenHeight = embedShareSettings?.screenHeight ?? window.screen.height;
				const screenWidth = embedShareSettings?.screenWidth ?? window.screen.width;
				const ratio = isMaintainProportions === false || queryParams?.fromQplayerWidget === 'true' ? screenHeight / screenWidth : videoRatio;
				const width = `calc(${ratio} * 100vh)`;

				playerElem.style.width = width;
				if (transitionElem) {
					transitionElem.style.width = width;
				}
				if (interactContainer && (isMaintainProportions === false || queryParams?.fromQplayerWidget === 'true')) {
					interactContainer.style.width = width;
				}
			});
		}
	}, [
		currentMedia,
		queryParams?.fromQplayerWidget,
		queryParams?.useLoader,
		isStoryPlayer,
		isMobile,
		embedShareSettings,
		playlistId,
		currentMediaId,
		isInlinePlayer,
		isMaintainProportions,
	]);

	const handleFullscreenchange = useCallback(() => {
		const isFullScreen =
			!!document.fullscreenElement ||
			(document as any).fullscreen ||
			(document as any).isFullScreen ||
			(document as any).msIsFullScreen ||
			(document as any).mozIsFullScreen ||
			(document as any).webkitIsFullScreen;
		if (!isFullScreen) {
			calculatePlayerSize();
		}
	}, [calculatePlayerSize]);

	useEffect(() => {
		let timeout: any = null;
		if (currentVolumeLevel > 0) {
			timeout = setTimeout(() => setShowUnmuteButton(false), 500);
		}
		return () => {
			timeout && clearTimeout(timeout);
		};
	}, [currentVolumeLevel]);

	useLayoutEffect(() => {
		calculatePlayerSize();
	}, [
		isMobile,
		isMaintainProportions,
		embedShareSettings.screenHeight,
		embedShareSettings.screenWidth,
		isInlinePlayer,
		embedShareSettings,
		playlistId,
		currentMediaId,
		isStoryPlayer,
		calculatePlayerSize,
	]);

	useEffect(() => {
		window.addEventListener('fullscreenchange', handleFullscreenchange);

		return () => {
			window.removeEventListener('fullscreenchange', handleFullscreenchange);
		};
	}, [handleFullscreenchange]);

	const renderUnmuteButton = () =>
		isPlaying && (
			<ControlButton
				tabIndex={0}
				vertical
				className={joinClassNames(
					showUnmuteButton ? '' : 'controlbar__control--vertical-hidden',
					isMobile ? 'controlbar__control--vertical-mobile' : '',
					'mute-button'
				)}
				icon={currentVolumeLevel > 0 ? <QPlayerSVGElement.Mute /> : <QPlayerSVGElement.Unmute />}
				onClick={showUnmuteButton ? onUnmuteClick : undefined}
				hoverStyle={hoverStyle}
			>
				{t('UNMUTE_BUTTON')}
			</ControlButton>
		);

	const showPostCover =
		!isLiveStopped || typeof isLiveStopped === 'boolean'
			? isLiveStopped
			: (isPlaying === undefined || (isPlaying && mediaType === 'scenario')) &&
				Math.ceil(NumberHelper.precision3(currentPosition)) === Math.ceil(NumberHelper.precision3(isLiveStopped));

	return minimizedPlayer || showCountdownModule || showPostCover || adsPlaying ? null : (
		<div
			className={joinClassNames(
				'controlsOverlay',
				isMobile ? 'controlsOverlay--mobile' : '',
				isInlinePlayer ? 'controlsOverlay--inline' : '',
				isStoryPlayer ? 'controlsOverlay--story' : ''
			)}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onFocus={onFocus}
			onBlur={onBlur}
		>
			{!isStoryPlayer && renderUnmuteButton()}

			{showTopControls &&
				(isStoryPlayer ? (
					<StoryTopControl
						className={joinClassNames([
							'controlsOverlayTopControls',
							settings?.controls?.alwaysVisible || playerHover ? 'controlsOverlayVisible' : '',
						])}
						settings={settings}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						queryParams={queryParams}
						isMobile={isMobile}
					/>
				) : (
					<TopControls
						className={joinClassNames([
							'controlsOverlayTopControls',
							settings?.topControls?.alwaysVisible || playerHover ? 'controlsOverlayVisible' : '',
						])}
						settings={settings}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						isInlinePlayer={isInlinePlayer}
						queryParams={queryParams}
						isMobile={isMobile}
					/>
				))}

			{isStoryPlayer && (
				<SideControls
					className={settings?.controls.alwaysVisible || playerHover ? 'controlsOverlayVisible' : ''}
					settings={settings}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					queryParams={queryParams}
					isMobile={isMobile}
					playerHover={playerHover}
				/>
			)}

			{isStoryPlayer ? (
				<StoryCenterControls playerHover={playerHover} isMobile={isMobile} settings={settings} queryParams={queryParams} />
			) : (
				<CenterControls
					playerHover={playerHover}
					isMobile={isMobile}
					settings={settings}
					isInlinePlayer={isInlinePlayer}
					queryParams={queryParams}
				/>
			)}

			{!isStoryPlayer && (
				<BottomControls
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onTouchStart={onTouchStart}
					onTouchMove={onTouchMove}
					playerHover={playerHover}
					settings={settings}
					isInlinePlayer={isInlinePlayer}
					isMaintainProportions={isMobile ? isMaintainProportions : undefined}
					alignVideoOnAndroidDevices={alignVideoOnAndroidDevices}
					queryParams={queryParams}
				/>
			)}
		</div>
	);
};

export default Controls;
